/**
 * QuestionnaireForm — type-specific fields for a "questionnaire" service
 * (TASK-332, split out of CreateServiceDialog.tsx in KAN-157).
 *
 * Renders the question-rows editor (name, type select, unit for number,
 * options for enum, description, required checkbox) plus client-side and
 * server-side validation errors.
 */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, X } from 'lucide-react';
import type { QuestionnaireFieldType } from '@/services/api/services';
import { QUESTION_TYPE_OPTIONS, type QuestionRow } from './types';

interface QuestionnaireFormProps {
  questionRows: QuestionRow[];
  onAddQuestionRow: () => void;
  onRemoveQuestionRow: (index: number) => void;
  onUpdateQuestionRow: (index: number, patch: Partial<QuestionRow>) => void;
  questionError: string | null;
  questionServerError: string | null;
}

export function QuestionnaireForm({
  questionRows,
  onAddQuestionRow,
  onRemoveQuestionRow,
  onUpdateQuestionRow,
  questionError,
  questionServerError,
}: QuestionnaireFormProps): JSX.Element {
  return (
    <div className="space-y-2">
      <Label>Вопросы опросника</Label>
      <p className="text-xs text-gray-500">
        Бот опросит клиента по этим параметрам и не будет называть цену. Для вопроса-документа бот попросит
        выслать документ на email компании.
      </p>
      <div data-testid="questionnaire-editor" className="space-y-2">
        {questionRows.map((row, index) => (
          <div
            key={index}
            data-testid={`question-row-${index}`}
            className="rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <Input
                data-testid="question-name"
                value={row.name}
                onChange={e => onUpdateQuestionRow(index, { name: e.target.value })}
                placeholder="Имя вопроса (например, площадь объекта)"
                className="h-8 flex-1"
              />
              <Select
                value={row.type}
                onValueChange={value =>
                  onUpdateQuestionRow(index, {
                    type: value as QuestionnaireFieldType,
                  })
                }>
                <SelectTrigger data-testid="question-type" className="h-8 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                data-testid="remove-row"
                onClick={() => onRemoveQuestionRow(index)}
                className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 flex-shrink-0"
                type="button"
                aria-label="Удалить вопрос">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {row.type === 'number' && (
                <Input
                  data-testid="question-unit"
                  value={row.unit}
                  onChange={e => onUpdateQuestionRow(index, { unit: e.target.value })}
                  placeholder="Ед. изм. (например, м²)"
                  className="h-8"
                />
              )}
              {row.type === 'enum' && (
                <Input
                  data-testid="question-options"
                  value={row.options}
                  onChange={e => onUpdateQuestionRow(index, { options: e.target.value })}
                  placeholder="Варианты через запятую"
                  className="h-8"
                />
              )}
              <Input
                data-testid="question-description"
                value={row.description}
                onChange={e => onUpdateQuestionRow(index, { description: e.target.value })}
                placeholder="Описание / подсказка боту"
                className="h-8"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
              <input
                type="checkbox"
                data-testid="question-required"
                checked={row.required}
                onChange={e => onUpdateQuestionRow(index, { required: e.target.checked })}
                className="rounded border-gray-300"
              />
              Обязательный
            </label>
          </div>
        ))}
      </div>
      <Button
        data-testid="add-question-row"
        variant="outline"
        size="sm"
        onClick={onAddQuestionRow}
        type="button">
        <Plus className="h-3.5 w-3.5 mr-1" />
        Добавить вопрос
      </Button>
      {questionError && (
        <p data-testid="error-question-name" className="text-sm text-red-500">
          {questionError}
        </p>
      )}
      {questionServerError && (
        <p data-testid="error-questionnaire-fields" className="text-sm text-red-500">
          {questionServerError}
        </p>
      )}
    </div>
  );
}
